import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Mail, RefreshCw, Loader2 } from "lucide-react";
import { useTRPC } from "@/lib/trpc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useRouter } from "next/navigation";

interface EmailVerificationStepProps {
  email: string;
  onNext?: () => void;
}

export function EmailVerificationStep({
  email,
  onNext,
}: EmailVerificationStepProps) {
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [checking, setChecking] = useState(false);
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const router = useRouter();

  const { data: verificationStatus } = useQuery({
    ...trpc.onboarding.checkEmailVerification.queryOptions(),
    refetchInterval: (query) => {
      // Stop polling once email is verified
      if (query.state.data?.emailVerified) {
        return false;
      }
      // Poll every 10 seconds (reduced from 5 seconds)
      return 10000;
    },
    staleTime: 5000, // Cache for 5 seconds to prevent rapid re-fetches
  });

  const resendEmailMutation = useMutation(
    trpc.user.sendEmailVerification.mutationOptions()
  );

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(
        () => setCountdown(countdown - 1),
        1000,
      );
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  // Auto-advance when email is verified
  useEffect(() => {
    if (verificationStatus?.emailVerified) {
      toast.success("Email verified!", { description: "Taking you to the next step..." });
      setTimeout(() => {
        if (onNext) {
          onNext();
        }
        router.push("/create-account/choose-plan");
      }, 1000);
    }
  }, [verificationStatus, onNext, toast, router]);

  const handleResend = async () => {
    try {
      await resendEmailMutation.mutateAsync();
      setCountdown(60);
      setCanResend(false);
      toast.success("Email sent", { description: "Check your inbox for the verification link" });
    } catch (error) {
      toast.error("Failed to resend email", { 
        description: error instanceof Error ? error.message : "Please try again later" 
      });
    }
  };

  const handleVerified = async () => {
    setChecking(true);
    try {
      // Force refresh the verification status
      await queryClient.invalidateQueries({
        queryKey: trpc.onboarding.checkEmailVerification.queryKey(),
      });
      
      const status = await queryClient.fetchQuery(
        trpc.onboarding.checkEmailVerification.queryOptions()
      );

      if (status.emailVerified) {
        toast.success("Email verified!", { description: "Proceeding to plan selection..." });
        if (onNext) {
          onNext();
        }
        router.push("/onboarding/plan");
      } else {
        toast.error("Email not verified yet", { description: "Please click the link in your email first" });
      }
    } catch (error) {
      toast.error("Error checking verification", { description: "Please try again" });
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="max-w-md mx-auto space-y-8">
      <div className="text-center space-y-6">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10">
          <Mail className="w-10 h-10" />
        </div>

        <div className="space-y-2">
          <h1 className="text-3xl font-mono">
            Check your email
          </h1>
          <p className="text-muted-foreground">
            We've sent a verification link to
          </p>
          <p className="font-mono">{email}</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="p-4 bg-muted/50 rounded border border-border space-y-2">
          <p className="text-sm">
            Click the link in the email to verify your account.
            The link will expire in 24 hours.
          </p>
        </div>

        <Button
          onClick={handleVerified}
          className="w-full font-mono"
          size="lg"
          disabled={checking || verificationStatus?.emailVerified}
        >
          {checking ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Checking...
            </>
          ) : verificationStatus?.emailVerified ? (
            "Email Verified ✓"
          ) : (
            "I've Verified My Email"
          )}
        </Button>

        <div className="text-center space-y-2">
          <p className="text-sm text-muted-foreground">
            Didn't receive the email?
          </p>
          <Button
            variant="ghost"
            onClick={handleResend}
            disabled={!canResend || resendEmailMutation.isPending}
            className="font-mono"
          >
            {resendEmailMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                {canResend
                  ? "Resend Email"
                  : `Resend in ${countdown}s`}
              </>
            )}
          </Button>
        </div>

        <div className="pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground text-center">
            Make sure to check your spam folder if you don't see
            the email
          </p>
        </div>
      </div>
    </div>
  );
}